import { Action, AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';

// Types
import {
  USER_AUTH0_LOADING,
  USER_AUTH0_SUCCESS,
  GET_USER_CURRENT,
  GET_USER_CURRENT_FAILURE,
  GET_USER_CURRENT_SUCCESS,
  SET_CURRENT_ORGANIZATION,
  SET_DEFAULT_ORGANIZATION,
  SET_CURRENT_PROVIDER,
} from './types';

// Core + Store
import { AppState } from 'store';
import { popToast } from 'store/toast/actions';
import { errorStickyToastOptions } from 'store/toast/constants';
import { handleServerError } from 'globals/utils/handleServerError';
import { IAuth0User, IUser, IUserOrganization, OrganizationKinds, PARSAction } from 'core/models';
import { CACHE_SELECTED_ORGANIZATION, CACHE_SELECTED_PROVIDER } from 'core/constants';

// Services.
import { CacheService } from 'services';
import { AnalyticsService } from 'services/AnalyticsService';
import { ProviderService } from 'services/ProviderService';
import { UserService } from 'services/UserService';

// action creators
export const loadUserAction = (): Action => ({
  type: USER_AUTH0_LOADING,
});

export const userSuccessAction = (user: IAuth0User): AnyAction => ({
  payload: user,
  type: USER_AUTH0_SUCCESS,
});

export const getCurrentUserSuccessAction = (payload: IUser): AnyAction => ({
  payload,
  type: GET_USER_CURRENT_SUCCESS,
});

export const getCurrentUserFailureAction = (payload: Error): AnyAction => ({
  payload,
  type: GET_USER_CURRENT_FAILURE,
});

export const getCurrentUserAction = (): Action => ({
  type: GET_USER_CURRENT,
});

export const setCurrentOrganizationAction = (payload: IUserOrganization): AnyAction => ({
  payload,
  type: SET_CURRENT_ORGANIZATION,
});

export const setDefaultOrganizationAction = (payload: IUserOrganization): AnyAction => ({
  payload,
  type: SET_DEFAULT_ORGANIZATION,
});

export const setCurrentProviderAction = (payload: IUserOrganization): AnyAction => ({
  payload,
  type: SET_CURRENT_PROVIDER,
});

export const getCurrentUser = (customerId?: string): ThunkAction<Promise<void>, AppState, null, PARSAction> => async (
  dispatch,
) => {
  await dispatch(getCurrentUserAction());

  try {
    const userService = new UserService();
    const user: IUser = await userService.getCurrentAsync();

    if (user?.id) {
      AnalyticsService.setUser(user.id, true);
    }

    // if the user doesn't have access to their default org (or they don't have one), guess at one
    const defaultOrganization: IUserOrganization =
      user.primaryUserOrganizations.find((o) => o.id === user.defaultOrganizationId) ??
      user.primaryUserOrganizations.find((o) => o.organizationKind === OrganizationKinds.SPECIAL_ORGANIZATION) ??
      user.primaryUserOrganizations.find((o) => o.organizationKind === OrganizationKinds.ACCREDITOR) ??
      user.primaryUserOrganizations[0];

    if (defaultOrganization) {
      // Set defaultOrganization.
      dispatch(setDefaultOrganizationAction(defaultOrganization));

      // Initialize currentOrganization with defaultOrganization.
      let currentOrganization: IUserOrganization = defaultOrganization;
      let currentProvider: IUserOrganization = null;
      // If the currentOrganization kind is accreditor or special organization,
      // we need to reset the currentOrganization to its first associated Provider.
      if (
        currentOrganization.organizationKind === OrganizationKinds.ACCREDITOR ||
        currentOrganization.organizationKind === OrganizationKinds.SPECIAL_ORGANIZATION
      ) {
        for (const organizationId of currentOrganization.childOrganizationIds) {
          const organization: IUserOrganization = user.primaryUserOrganizations.find((o) => o.id === organizationId);
          if (organization?.organizationKind === OrganizationKinds.PROVIDER) {
            currentOrganization = organization;
            break;
          }
        }
      }

      // Handle context passed from armature.
      if (customerId) {
        const armatureOrganization: IUserOrganization = user.primaryUserOrganizations.find(
          (o) => o.customerId === customerId,
        );
        if (armatureOrganization) {
          CacheService.set(CACHE_SELECTED_ORGANIZATION, armatureOrganization.id);
          CacheService.remove(CACHE_SELECTED_PROVIDER);
        }
      }

      // If user has a selected organization id, use that.
      const selectedOrganizationId = CacheService.get(CACHE_SELECTED_ORGANIZATION);
      if (selectedOrganizationId) {
        for (const organization of user.primaryUserOrganizations) {
          if (organization.id === selectedOrganizationId) {
            currentOrganization = organization;
            break;
          }
        }
      }

      // If user has a selected provider id, use that.
      const selectedProviderId = CacheService.get(CACHE_SELECTED_PROVIDER);
      if (selectedProviderId) {
        for (const provider of user.secondaryUserOrganizations) {
          if (provider.id === selectedProviderId) {
            currentProvider = provider;
            break;
          }
        }
      }

      // Depending on the available Organization/Provider Ids, set the Organization-Id/Provider-Id headers
      const providerService = new ProviderService();
      providerService.currentOrganizationId = currentOrganization?.id;

      // if you don't have a provider selected, we shouldn't send one, unless your selected organization *is* a provider
      providerService.currentProviderId =
        currentProvider?.id ??
        (currentOrganization?.organizationKind === OrganizationKinds.PROVIDER ? currentOrganization?.id : undefined);

      // If there's no current organization, the user likely has an invalid account
      if (!currentOrganization) {
        await dispatch(
          popToast({
            ...errorStickyToastOptions,
            message: <>Unexpected account error occurred. Please logout to continue</>,
          }),
        );
      }
      // Set currentOrganization.
      await dispatch(setCurrentOrganizationAction(currentOrganization));
      await dispatch(setCurrentProviderAction(currentProvider));
    } else {
      await dispatch(
        popToast({
          ...errorStickyToastOptions,
          message: <>Unexpected account error occurred. Please logout to continue</>,
        }),
      );
    }
    await dispatch(getCurrentUserSuccessAction(user));
  } catch (error) {
    handleServerError({ error, thunkName: 'getCurrentUser' });
    await dispatch(getCurrentUserFailureAction(error));
  }
};
